import { TextField, TextFieldProps } from "@mui/material";
import { ChangeEvent } from "react";
import {
  Control,
  Controller,
  FieldValues,
  Path,
  RegisterOptions,
} from "react-hook-form";

type HtmlEvent = ChangeEvent<HTMLTextAreaElement | HTMLInputElement>;

interface FormTextFieldProps<T extends FieldValues>
  extends Omit<TextFieldProps, "name"> {
  name: Path<T>;
  control: Control<T>;
  label?: string;
  handleChange?: (e: HtmlEvent, fieldOnChange: (e: HtmlEvent) => void) => void;
  rules?: RegisterOptions<T>;
}

const FormTextField = <T extends FieldValues>({
  name,
  control,
  label,
  defaultValue,
  handleChange,
  rules = {},
  ...rest
}: FormTextFieldProps<T>) => {
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState: { error } }) => {
        const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
          if (e.key === "Enter" && e.shiftKey) {
            e.preventDefault();
            const textarea = e.target as HTMLTextAreaElement;
            const { selectionStart, selectionEnd } = textarea;

            field.onChange(() => {
              return (
                field.value.slice(0, selectionStart) +
                "\n" +
                field.value.slice(selectionEnd)
              );
            });

            setTimeout(() => {
              textarea.selectionStart = textarea.selectionEnd =
                selectionStart + 1;
            }, 0);
          }
        };

        return (
          <TextField
            {...field}
            helperText={error ? error.message : null}
            label={label}
            error={!!error}
            onKeyDown={handleKeyDown}
            onChange={(e) =>
              handleChange ? handleChange(e, field.onChange) : field.onChange(e)
            }
            fullWidth
            variant="outlined"
            {...rest}
          />
        );
      }}
    />
  );
};

export default FormTextField;
